import { saveBinaryFile } from "../renderer/platform";

const exportReportToExcel = async ({ reportType, reportData, fileName }) => {
  if (!reportData) return;

  await new Promise(r => setTimeout(r, 0));

  // ExcelJS is substantial and report export is user initiated. Loading it here
  // keeps the normal desktop/login startup bundle small.
  const excelModule = await import("exceljs");
  const ExcelJS = excelModule.default ?? excelModule;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Report");

  ws.columns = [
    { width: 36 },
    { width: 18 },
    { width: 22 },
  ];

  const border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  const addTitle = (text) => {
    const row = ws.addRow([text]);
    ws.mergeCells(`A${row.number}:C${row.number}`);
    row.font = { bold: true, size: 14 };
    row.alignment = { horizontal: "center" };
    row.height = 26;
    ws.addRow([]);
  };

  const addHeader = (headers) => {
    const row = ws.addRow(headers);
    row.font = { bold: true };
    row.alignment = { horizontal: "center" };
    row.eachCell(c => (c.border = border));
  };

  const addRow = (values) => {
    const row = ws.addRow(values);
    row.eachCell((c, i) => {
      c.border = border;
      c.alignment = { horizontal: i === 1 ? "center" : "left" };
    });
  };

  /* ===== COMMON SUMMARY ===== */
  addTitle(
    reportType === "daily"
      ? "DAILY SALES REPORT"
      : reportType === "monthly"
      ? "MONTHLY SALES REPORT"
      : "CUSTOM DATE RANGE REPORT"
  );

  addRow(["Total Sales", "", reportData.totalSales]);
  addRow(["Total Orders", "", reportData.totalOrders]);
  ws.addRow([]);

  /* ===== ORDER TYPE ===== */
  addTitle("ORDER TYPE BREAKDOWN");
  addHeader(["Type", "Orders", "Revenue"]);
  addRow(["Dine In", reportData.orderTypeBreakdown?.dineIn?.count ?? 0, reportData.orderTypeBreakdown?.dineIn?.total ?? 0]);
  addRow(["Take Away", reportData.orderTypeBreakdown?.takeAway?.count ?? 0, reportData.orderTypeBreakdown?.takeAway?.total ?? 0]);
  ws.addRow([]);

  /* ===== ITEM SALES ===== */
  addTitle("ITEM SALES");
  addHeader(["Item Name", "Quantity", "Revenue"]);
  Object.entries(reportData.itemSales || {}).forEach(([n, d]) =>
    addRow([n, d.quantity ?? 0, d.total ?? 0])
  );
  ws.addRow([]);

  /* ===== CASHIER ===== */
  addTitle("CASHIER PERFORMANCE");
  addHeader(["Cashier", "Orders", "Revenue"]);
  Object.entries(reportData.cashierSales || {}).forEach(([n, d]) =>
    addRow([n, d.orders ?? 0, d.total ?? 0])
  );
  ws.addRow([]);

  /* ===== DAILY ONLY ===== */
  if (reportType === "daily") {
    addTitle("HOURLY SALES");
    addHeader(["Hour", "Orders", "Revenue"]);
    reportData.hourlySales?.forEach(h =>
      addRow([h.hour, h.purchases ?? 0, h.sales ?? 0])
    );
  }

  /* ===== MONTHLY ONLY ===== */
  if (reportType === "monthly") {
    addTitle("DAILY SALES");
    addHeader(["Date", "Orders", "Revenue"]);
    Object.entries(reportData.dailySales || {}).forEach(([day, d]) =>
      addRow([day, d.orders ?? 0, d.total ?? 0])
    );
  }

  const buffer = await wb.xlsx.writeBuffer();

  await saveBinaryFile({
    suggestedName: `${fileName}.xlsx`,
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    data: new Uint8Array(buffer)
  });
};

export default exportReportToExcel;
